/**
 * Reusable summary-row components for entity detail page headers.
 *
 * Promoted to a shared module 2026-06-09 (Phase A3.a) so the same summary
 * pattern can be applied across detail pages — currently AgreementDetailPage
 * has the canonical implementation; A3.b/d/h add equivalent boxes to
 * AccountDetailPage / AccountInsuranceDetailPage / PersonDetailPage.
 *
 * SummaryItem  — plain "LABEL / value" cell (uppercase dimmed label, bold value).
 *                Extended in A3.a with optional `to` (renders value as a Link),
 *                `annotation` (small muted suffix, e.g. "via Policy"), and
 *                `muted` (renders value in dimmed color, for derived parent
 *                fields like the effective Agreement of an Indirect Account).
 * SummaryBadge — coloured-badge slot with optional multiline tooltip; used for
 *                PEP / US-Person aggregations on Agreement detail today.
 *
 * Reference design: AgreementDetailPage's summary box (see Pedro's
 * screenshots in the 2026-06-09 conversation) — uppercase dimmed labels +
 * bold mono-or-prop values arrayed horizontally, with a Status badge
 * anchoring the right end.
 */
import { Anchor, Badge, Text, Tooltip } from '@mantine/core';
import { Link } from 'react-router-dom';

interface SummaryItemProps {
  /** Uppercase dimmed label (e.g. "AGREEMENT", "BANK"). */
  label: string;
  /** The value to render. `null` or empty string renders as em-dash. */
  value: string | number | null;
  /** Render value in monospace (e.g. for IBAN, code_1). */
  mono?: boolean;
  /** Larger, bolder value (e.g. for the primary identifier on the row). */
  bold?: boolean;
  /**
   * If set, the value renders as a clickable Mantine `<Anchor component={Link}>`
   * pointing to this path. Used to make parent-entity fields navigable
   * (e.g. clicking Agreement on Account detail jumps to that Agreement).
   */
  to?: string;
  /**
   * Optional small dimmed suffix rendered after the value. Used for the
   * "via Policy" annotation on the Agreement field of an Indirect Account
   * (Phase A3.b), where the Agreement is derived through the Policy.
   */
  annotation?: string;
  /**
   * When true, the value is rendered with dimmed/secondary color. Used on
   * derived fields (e.g. an Indirect Account's effective Agreement) to
   * visually distinguish them from primary identifying fields on the same
   * row.
   */
  muted?: boolean;
  /**
   * When true, both the label and the value are center-aligned within the
   * cell. Added 2026-06-09 (Phase A3.h polish) for the Person header's
   * party-count rollups — centered numerals under their labels read more
   * naturally than left-aligned ones, especially when adjacent to other
   * left-aligned identity fields like Name / Kind.
   */
  centered?: boolean;
}

export function SummaryItem({
  label, value, mono = false, bold = false, to, annotation, muted = false, centered = false,
}: SummaryItemProps) {
  const display = value === null || value === '' ? '—' : value;
  const isEmDash = display === '—';
  // Em-dash is always dimmed regardless of caller's `muted` choice;
  // a populated value uses `muted` to decide.
  const valueColor = isEmDash ? 'dimmed' : (muted ? 'dimmed' : undefined);
  const fontWeight = bold ? 700 : 600;
  const fontSize = bold ? 'lg' : undefined;
  const fontFamily = mono ? 'monospace' : undefined;

  const valueNode = to && !isEmDash ? (
    <Anchor component={Link} to={to} fw={fontWeight} size={fontSize} ff={fontFamily} c={valueColor}>
      {display}
    </Anchor>
  ) : (
    <Text fw={fontWeight} size={fontSize} ff={fontFamily} c={valueColor} span>
      {display}
    </Text>
  );

  return (
    <div style={centered ? { textAlign: 'center' } : undefined}>
      <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
      <Text component="div">
        {valueNode}
        {annotation && (
          <Text size="xs" c="dimmed" ml={4} span>{annotation}</Text>
        )}
      </Text>
    </div>
  );
}

interface SummaryBadgeProps {
  label: string;
  badge: string;
  color: string;
  /** Optional multi-line tooltip rendered on hover. */
  tooltip?: string;
}

export function SummaryBadge({ label, badge, color, tooltip }: SummaryBadgeProps) {
  const node = (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
      <Badge
        color={color}
        variant={color === 'gray' || color === 'green' ? 'light' : 'filled'}
        size="md"
        style={{ cursor: tooltip ? 'help' : 'default' }}
      >
        {badge}
      </Badge>
    </div>
  );
  if (!tooltip) return node;
  return (
    <Tooltip label={tooltip} multiline w={320}
      styles={{ tooltip: { whiteSpace: 'pre-line' } }}>
      {node}
    </Tooltip>
  );
}
